"use client";

import {alpha, Box, Rating, Stack, Typography} from "@mui/material";
import React from "react";
import Image from "next/image";
import useWistiaScript from "@/hooks/media/use-wistia-scripts";
import AutoCompleteLocationSearchBar from "@/components/homepage/auto-complete-location-search-bar";
import sanitizeHtml from "@/utils/sanitize-html";
import { removeFirstPTag } from "@/utils/content-utils";
import BackgroundVideo, { TransitionStrategy } from "./background-video";

interface HomePageHeroSectionProps {
  heroData: any;
  posterImageBase64?: string | null;
  transitionStrategy?: TransitionStrategy; // Allow testing different transition strategies
}

const HomePageHeroSection = ({
  heroData,
  posterImageBase64,
  transitionStrategy = "pause-reset-play" // Wistia recommended: pause, reset to frame 0, then play
}: HomePageHeroSectionProps) => {
  const { basicInfo, backgroundVideo, backgroundFallbackImage, feefoSummary } = heroData || {};
  const video = backgroundVideo?.videoId || "";
  const startMuted = true;
  useWistiaScript();

  const wistiaClassName = video
    ? `wistia_embed wistia_async_${video} ${startMuted ? "muted=true" : "muted=false"}  stillUrl=${
        backgroundFallbackImage?.imageUrl
      } autoplay=true volumeControl=false playbar=false  playButton=false settingsControl=false qualityControl=false captionsControl=false smallPlayButton=false controlsVisibleOnLoad=false  fullscreenButton=false fitStrategy=cover endVideoBehavior=loop
    `
    : "";

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        height: "628px",
        width: "100%",
        overflow: "hidden",
        pointerEvents: "none",
        "& button, & a, & input": {
          pointerEvents: "auto" // But keep these interactive
        }
      }}
      data-track="visibility"
      data-module-name="Hero Section"
      data-ranking-algorithm="manual"
      data-module-ranking-position="1"
      data-testid="hero-section"
    >
      <BackgroundVideo
        video={video}
        className={wistiaClassName}
        posterImageBase64={posterImageBase64}
        transitionStrategy={transitionStrategy}
      />
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 2,
          width: "100%",
          height: "628px",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          backgroundColor: (theme) => alpha(theme.palette.common.black, 0.2)
        }}
      >
        <Stack
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "10rem",
            padding: {
              xs: "2.4rem 2.4rem 4rem",
              md: "2.4rem 2.4rem 5rem"
            }
          }}
        >
          <Stack gap={{ xs: "50px", md: "60px" }} alignItems="center">
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h1"
                color="common.white"
                sx={{ fontSize: { xs: "32px", sm: "44px" }, fontWeight: 700, lineHeight: "150%" }}
                data-testid="hero-title"
                dangerouslySetInnerHTML={{ __html: removeFirstPTag(sanitizeHtml(basicInfo?.headerText)) }}
              />
              <Typography
                variant="h4"
                color="common.white"
                data-testid="hero-subtitle"
                sx={{
                  fontSize: { xs: "24px", sm: "30px" },
                  fontWeight: 500,
                  lineHeight: "150%",
                  marginY: "3rem"
                }}
                dangerouslySetInnerHTML={{
                  __html: removeFirstPTag(sanitizeHtml(basicInfo?.copyText ?? ""))
                }}
              />
            </Box>
            <Box>
              <AutoCompleteLocationSearchBar />
            </Box>
          </Stack>
          <Stack gap="12px" alignItems="center">
          <Rating
            value={feefoSummary?.stars || 0}
            precision={0.1}
            readOnly
            data-testid="feefo-rating"
            size="medium"
            sx={{
              "& .MuiRating-iconFilled": {
                color: "common.white",
                marginRight: "5px"
              }
            }}
          />
          <Stack direction="row" alignItems="center" justifyContent="center">
            <Typography
              variant="h2"
              color="common.white"
              fontWeight={500}
              data-testid='"feefo-review-count'
              sx={{ fontSize: "18px !important " }}
            >
              {heroData?.feefoSummary?.count ? `${heroData.feefoSummary.count}+ reviews on` : ""}
            </Typography>
            {heroData?.feefoImageUrl && (
              <Image src={heroData.feefoImageUrl} alt="Feefo Logo" width={77} height={27} />
            )}
          </Stack>
        </Stack>
        </Stack>


      </Box>
    </Box>
  );
};

export default HomePageHeroSection;
